/**
 * Publishes where fixed top-anchored chrome (the mobile HealthBanner) may
 * start, as the `--mol-fixed-top-offset` CSS variable — the page header's
 * bottom edge in viewport px. Pages with a header (the workspace's in-flow
 * bar, the global fixed Header) publish it while mounted so the banner slots
 * BELOW the header instead of covering it; headerless pages (discovery) leave
 * it unset and the banner sits at the very top.
 *
 * @module
 */

import { type RefObject, useEffect } from 'react'

/**
 * Tracks `ref`'s bottom edge (ResizeObserver + window resize) into
 * `--mol-fixed-top-offset` while `enabled`; clears it on unmount/disable.
 * @param ref - The header element to measure.
 * @param enabled - Publish only when true (typically `isMobile`).
 */
export function usePublishFixedTopOffset(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return
    const update = (): void => {
      document.documentElement.style.setProperty(
        '--mol-fixed-top-offset',
        `${Math.max(0, Math.round(el.getBoundingClientRect().bottom))}px`,
      )
    }
    update()
    // jsdom (tests) has no ResizeObserver; the window resize listener still
    // keeps the offset current there and in any engine without it.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    ro?.observe(el)
    window.addEventListener('resize', update)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', update)
      document.documentElement.style.removeProperty('--mol-fixed-top-offset')
    }
  }, [ref, enabled])
}
